// Which pipeline faults mock mode gives the mock account.
//
// `FeedHealthBanner` is the highest-priority element on the dashboard — it is
// the only surface that tells a publisher their catalogue has gone dark — and
// until now it could not be rendered in mock mode at all, because the fixture
// serves a healthy account. So the one branch that was LIVE IN PRODUCTION for
// the only activated publisher (a struck-out feed, 2026-09-06) had never been
// seen by anyone assessing the screen, at any viewport.
//
// The default stays EMPTY, exactly as `parseMockFlags` does and for the same
// reason: a healthy account is the honest default, and this is the opt-in that
// makes an unhealthy one reachable, not a changed default.
//
//   REACT_APP_MOCK_FAULTS=paused,stale,broken npm run dev:user
//
// The three are separate because their REMEDIES differ — a struck-out feed is
// repaired or resumed, an out-of-date upload can only be uploaded again, and a
// refused credential is reconnected on another screen entirely — so each has to
// be renderable on its own, not only all together.
//
// Its own module because `apiService.ts` imports `openapi-fetch`, which is
// ESM-only and untransformed by CRA's jest — anything importing it cannot be
// unit tested here.
//
// `unmapped` is the fourth and is about a different pipeline stage: the feed
// INSPECT step, whose column-review table renders either "Columns recognised"
// (nothing to choose) or "Check your columns" (dropdowns the publisher must
// set). The second is the branch that matters — it is the one with twelve
// controls on it, and it is where a reported bug once survived its own fix —
// and a fixture that only ever auto-resolves cannot render it. Same reason as
// the three above: the interesting branch must be reachable on its own.
// `no-baseline` is the fifth, and it is about Analytics: the previous period
// begins before the account has data, so no delta from it is meaningful. It is
// what EVERY new publisher sees for their first month, and what the live account
// was showing — as growth — when it was measured on 2026-09-07.
// `no-data` is the sixth, and it is the state EVERY publisher sees first: the
// widget is installed (or is not) and nothing has come back yet. Analytics
// renders five zeros for it, and until this switch existed the mock always
// served a busy account, so nobody reviewing the screen had seen the first thing
// a new publisher sees. Third instance of the same fixture fault in three
// passes — `neverClicked`, `no-baseline`, and now this.
// `repairing` is the seventh: a managed feed WE are rebuilding
// (managed-feed-self-repair). The mock's struck-out sofiline row is served as
// `repairing` instead — the exact change the pilot's four stopped shops see
// after deploy, and a state no other fixture can reach.
export type MockFault = 'paused' | 'stale' | 'broken' | 'unmapped' | 'no-baseline' | 'no-data' | 'repairing';

const KNOWN: MockFault[] = ['paused', 'stale', 'broken', 'unmapped', 'no-baseline', 'no-data', 'repairing'];

export function parseMockFaults(value: string | undefined): MockFault[] {
  return (value ?? '')
    .split(',')
    .map(name => name.trim())
    // An empty value splits to [''], and an unrecognised name can render
    // nothing anyway — both are dropped, so callers can test membership OR
    // length and get the same answer.
    .filter((name): name is MockFault => (KNOWN as string[]).includes(name));
}
